<script setup lang="ts">
/**
 * Stage path of a pipeline (lead → offer → order …): chevron steps that read
 * as done, current or upcoming, with an optional count per stage and a
 * `skipped`/`lost` stage drawn apart. Stages before `current` are done unless
 * a stage says otherwise. With `clickable` the stages are buttons (`select`).
 *
 * It measures its own width: below `compactBelow` px the strip folds into
 * „Stufe 3 von 6: Angebot“ with a row of segments. For a wizard with numbered
 * circles and slotted steps use NxStepper — this is the flat chevron strip of
 * record pages and pipelines.
 */
import { computed, useTemplateRef } from 'vue';
import { useChartSize } from '../Chart/useChartSize';
import { NxNativeSelect } from '../Inputs';

export type NxStageState = 'done' | 'current' | 'upcoming' | 'skipped' | 'lost';

export interface NxStage {
  key: string | number;
  label: string;
  /** A figure on the stage (records in it). */
  count?: number;
  /** Overrides the state derived from `current`. */
  state?: NxStageState;
}

const props = withDefaults(
  defineProps<{
    stages: readonly NxStage[];
    /** Key of the current stage; `null` for none reached yet. */
    current?: NxStage['key'] | null;
    clickable?: boolean;
    /** Own width (px) below which the strip folds into one line. */
    compactBelow?: number;
    /** `{n}`, `{total}`, `{label}` are replaced. */
    compactLabel?: string;
    ariaLabel?: string;
    /** Spoken states. */
    doneLabel?: string;
    currentLabel?: string;
    skippedLabel?: string;
    lostLabel?: string;
  }>(),
  {
    current: null,
    clickable: false,
    compactBelow: 480,
    compactLabel: 'Stufe {n} von {total}: {label}',
    ariaLabel: 'Stufen',
    doneLabel: 'erledigt',
    currentLabel: 'aktuell',
    skippedLabel: 'übersprungen',
    lostLabel: 'verloren',
  }
);

const emit = defineEmits<{ select: [key: NxStage['key']] }>();

const currentIndex = computed(() => props.stages.findIndex((s) => s.key === props.current));

const resolved = computed(() =>
  props.stages.map((stage, i) => {
    let state: NxStageState = stage.state ?? 'upcoming';
    if (!stage.state && currentIndex.value >= 0) {
      if (i < currentIndex.value) state = 'done';
      else if (i === currentIndex.value) state = 'current';
    }
    return { ...stage, index: i, state };
  })
);

const rootRef = useTemplateRef<HTMLElement>('root');
const { width } = useChartSize(rootRef, { width: 800, height: 0 });
const compact = computed(() => width.value < props.compactBelow);

const compactText = computed(() => {
  const i = currentIndex.value;
  const stage = props.stages[i];
  if (!stage) return '';
  return props.compactLabel
    .replace('{n}', String(i + 1))
    .replace('{total}', String(props.stages.length))
    .replace('{label}', stage.label);
});

/** Folded but still clickable: every stage as an option of one select. */
const compactOptions = computed(() =>
  props.stages.map((stage, i) => ({
    value: stage.key,
    label: props.compactLabel
      .replace('{n}', String(i + 1))
      .replace('{total}', String(props.stages.length))
      .replace('{label}', stage.label),
  }))
);
function onCompactSelect(key: string | number | null) {
  if (key !== null && key !== props.current) emit('select', key);
}

function spoken(state: NxStageState): string {
  if (state === 'done') return props.doneLabel;
  if (state === 'current') return props.currentLabel;
  if (state === 'skipped') return props.skippedLabel;
  if (state === 'lost') return props.lostLabel;
  return '';
}
</script>

<template>
  <nav
    ref="root"
    class="nx-stage-path"
    :class="{ 'is-compact': compact }"
    :aria-label="props.ariaLabel"
    data-testid="nx-stage-path"
  >
    <div v-if="compact" class="nx-stage-path__compact">
      <NxNativeSelect
        v-if="props.clickable"
        class="nx-stage-path__compact-select"
        size="full"
        :model-value="props.current"
        :options="compactOptions"
        :aria-label="props.ariaLabel"
        data-testid="nx-stage-path-select"
        @update:model-value="onCompactSelect"
      />
      <span v-else class="nx-stage-path__compact-text">{{ compactText }}</span>
      <span class="nx-stage-path__segments" aria-hidden="true">
        <span
          v-for="stage in resolved"
          :key="stage.key"
          class="nx-stage-path__segment"
          :class="`state-${stage.state}`"
        />
      </span>
    </div>
    <ol v-else class="nx-stage-path__list">
      <li
        v-for="stage in resolved"
        :key="stage.key"
        class="nx-stage-path__stage"
        :class="[`state-${stage.state}`, { 'is-first': stage.index === 0 }]"
        :aria-current="stage.state === 'current' ? 'step' : undefined"
        data-testid="nx-stage"
      >
        <component
          :is="props.clickable ? 'button' : 'span'"
          :type="props.clickable ? 'button' : undefined"
          class="nx-stage-path__body"
          @click="props.clickable && emit('select', stage.key)"
        >
          <span class="nx-stage-path__label">{{ stage.label }}</span>
          <span v-if="stage.count !== undefined" class="nx-stage-path__count">
            {{ stage.count }}
          </span>
          <span v-if="spoken(stage.state)" class="nx-stage-path__sr">
            ({{ spoken(stage.state) }})
          </span>
        </component>
      </li>
    </ol>
  </nav>
</template>

<style scoped>
.nx-stage-path {
  --chevron: calc(var(--nxt-size-unit) * 0.6);

  min-width: 0;
}
.nx-stage-path__list {
  display: flex;
  gap: calc(var(--nxt-hairline) * 2);
  margin: 0;
  padding: 0;
  font-size: inherit;
  list-style: none;
}
.nx-stage-path__stage {
  --stage-bg: var(--nxt-surface-sunken-bg);
  --stage-fg: var(--nxt-fg-subtle);
  --stage-count-bg: var(--nxt-surface-bg);

  display: flex;
  /* Content-sized: a long label takes the room a short one leaves. */
  flex: 1 1 auto;
  min-width: 0;
}
.nx-stage-path__stage.state-done {
  --stage-bg: var(--nxt-brand-subtle-bg);
  --stage-fg: var(--nxt-brand-subtle-fg);
}
.nx-stage-path__stage.state-current {
  --stage-bg: var(--nxt-brand-bg);
  --stage-fg: var(--nxt-brand-fg);
  --stage-count-bg: color-mix(in srgb, var(--nxt-brand-fg) 22%, transparent);
}
.nx-stage-path__stage.state-skipped {
  --stage-fg: var(--nxt-fg-muted);
}
.nx-stage-path__stage.state-lost {
  --stage-bg: var(--nxt-danger-subtle-bg);
  --stage-fg: var(--nxt-danger-subtle-fg);
}
.nx-stage-path__body {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
  height: var(--nxt-control-size-md);
  padding: 0 calc(var(--chevron) + var(--nxt-space-xs));
  border: 0;
  background: var(--stage-bg);
  color: var(--stage-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  clip-path: polygon(
    0 0,
    calc(100% - var(--chevron)) 0,
    100% 50%,
    calc(100% - var(--chevron)) 100%,
    0 100%,
    var(--chevron) 50%
  );
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    filter var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-stage-path__stage.is-first .nx-stage-path__body {
  padding-inline-start: var(--nxt-space-md);
  border-start-start-radius: var(--nxt-radius-md);
  border-end-start-radius: var(--nxt-radius-md);
  clip-path: polygon(
    0 0,
    calc(100% - var(--chevron)) 0,
    100% 50%,
    calc(100% - var(--chevron)) 100%,
    0 100%
  );
}
.nx-stage-path__stage.state-current .nx-stage-path__body {
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-stage-path__stage.state-skipped .nx-stage-path__label {
  text-decoration: line-through;
}
button.nx-stage-path__body {
  cursor: pointer;
}
button.nx-stage-path__body:hover {
  filter: brightness(0.96);
}
button.nx-stage-path__body:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -3);
}
.nx-stage-path__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-stage-path__count {
  flex: 0 0 auto;
  min-width: calc(var(--nxt-size-unit) * 1.25);
  padding: 0 var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-full);
  background: var(--stage-count-bg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
  line-height: 1.6;
  text-align: center;
}
.nx-stage-path__sr {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
.nx-stage-path__compact {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}
.nx-stage-path__compact-text {
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-stage-path__segments {
  display: flex;
  gap: calc(var(--nxt-hairline) * 2);
}
.nx-stage-path__segment {
  flex: 1 1 0;
  height: calc(var(--nxt-hairline) * 4);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-chart-track);
}
.nx-stage-path__segment.state-done {
  background: var(--nxt-brand-border);
}
.nx-stage-path__segment.state-current {
  background: var(--nxt-brand-bg);
}
.nx-stage-path__segment.state-lost {
  background: var(--nxt-danger-bg);
}
@media (prefers-reduced-motion: reduce) {
  .nx-stage-path__body {
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-stage-path__stage.state-current .nx-stage-path__body {
    outline: calc(var(--nxt-hairline) * 2) solid Highlight;
  }
}
</style>
